import React, { useEffect, useState } from "react";
import axios from "../axios";

/**
 * VendorLinkedProducts
 * Affiche et gère les VendorProductLink du vendeur connecté.
 *
 * Fonctionnalités :
 * - Chargement des liens au montage
 * - Recherche par nom ou barcode
 * - Modification inline (prix, stock, note)
 * - Suppression d'un lien
 *
 * Props :
 *   user       — objet utilisateur connecté (id, token)
 *   refreshKey — (optionnel) incrémenter pour forcer un rechargement depuis le parent
 */
// Map valeur DB → libellé affiché — alignée sur les options du <select> d'édition
const STOCK_LABELS = {
  "en stock":     "Disponible",
  "promotion":    "Promotion",
  "indisponible": "Indisponible"
};

const VendorLinkedProducts = ({ user, refreshKey }) => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  // linkId en cours d'édition (null = aucun)
  const [editingId, setEditingId] = useState(null);
  // Formulaire d'édition en cours
  const [editForm, setEditForm] = useState({ price: "", stock: "en stock", customNote: "" });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const vendorId = user?.id || user?._id;
  const token = user?.token;
  const authHeader = { Authorization: `Bearer ${token}` };

  // ─────────────────────────────────────────
  // Chargement des liens du vendeur
  // ─────────────────────────────────────────
  useEffect(() => {
    if (!vendorId) return;

    const fetchLinks = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`/vendor-links/${vendorId}`, { headers: authHeader });
        setLinks(res.data || []);
        console.log(`✅ [VendorLinkedProducts] ${res.data.length} lien(s) chargé(s)`);
      } catch (err) {
        console.error("❌ [VendorLinkedProducts] Erreur chargement :", err);
        setError("Impossible de charger les produits liés.");
      } finally {
        setLoading(false);
      }
    };

    fetchLinks();
  }, [vendorId, refreshKey]); // refreshKey permet au parent de forcer un rechargement

  // ─────────────────────────────────────────
  // Filtrage local par nom ou barcode
  // ─────────────────────────────────────────
  const filtered = links.filter((link) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const name = link.globalProductId?.name?.toLowerCase() || "";
    const barcode = link.globalProductId?.barcode?.toLowerCase() || "";
    return name.includes(q) || barcode.includes(q);
  });

  // ─────────────────────────────────────────
  // Ouvrir le formulaire d'édition
  // ─────────────────────────────────────────
  const startEdit = (link) => {
    setEditingId(link._id);
    setEditForm({
      price: link.price != null ? link.price : "",
      stock: link.stock || "en stock",
      customNote: link.customNote || ""
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ price: "", stock: "en stock", customNote: "" });
  };

  // ─────────────────────────────────────────
  // Sauvegarder les modifications d'un lien
  // ─────────────────────────────────────────
  const saveEdit = async (linkId) => {
    setSaving(true);
    try {
      const res = await axios.put(
        `/vendor-links/${linkId}`,
        {
          price: editForm.price !== "" ? parseFloat(editForm.price) : null,
          stock: editForm.stock,
          customNote: editForm.customNote
        },
        { headers: authHeader }
      );

      // Mise à jour locale immédiate sans rechargement complet
      setLinks(prev =>
        prev.map(l => l._id === linkId ? { ...l, ...res.data } : l)
      );
      setEditingId(null);
      console.log(`✅ [VendorLinkedProducts] Lien ${linkId} mis à jour`);
    } catch (err) {
      console.error("❌ [VendorLinkedProducts] Erreur mise à jour :", err);
      alert("Erreur lors de la mise à jour.");
    } finally {
      setSaving(false);
    }
  };

  // ─────────────────────────────────────────
  // Supprimer un lien
  // ─────────────────────────────────────────
  const deleteLink = async (linkId, productName) => {
    if (!window.confirm(`Supprimer le lien avec "${productName}" ?`)) return;
    setDeletingId(linkId);
    try {
      await axios.delete(`/vendor-links/${linkId}`, { headers: authHeader });
      setLinks(prev => prev.filter(l => l._id !== linkId));
      console.log(`✅ [VendorLinkedProducts] Lien ${linkId} supprimé`);
    } catch (err) {
      console.error("❌ [VendorLinkedProducts] Erreur suppression :", err);
      alert("Erreur lors de la suppression.");
    } finally {
      setDeletingId(null);
    }
  };

  // ─────────────────────────────────────────
  // Rendu
  // ─────────────────────────────────────────
  return (
    <div className="section-card">
      <h3 className="text-lg font-semibold mb-4">🔗 Mes produits barcode liés</h3>

      {/* Champ de recherche */}
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Rechercher par nom ou code-barres..."
        className="input-field w-full mb-4"
      />

      {/* États de chargement / erreur */}
      {loading && <p className="text-sm text-gray-400">⏳ Chargement...</p>}
      {error && <p className="text-sm text-red-500">{error}</p>}

      {/* Liste vide */}
      {!loading && !error && filtered.length === 0 && (
        <p className="text-sm text-gray-500 italic">
          {search ? "Aucun résultat pour cette recherche." : "Aucun produit barcode lié pour l'instant."}
        </p>
      )}

      {/* Liste des liens */}
      {!loading && filtered.length > 0 && (
        <ul className="space-y-4">
          {filtered.map((link) => {
            const gp = link.globalProductId; // GlobalProduct populé
            const isEditing = editingId === link._id;
            const isDeleting = deletingId === link._id;

            return (
              <li key={link._id} className="border rounded-lg p-4 bg-gray-50">

                {/* ── En-tête : image + infos produit global ── */}
                <div className="flex gap-4 items-start mb-3">
                  {gp?.imageUrl && (
                    <img
                      src={gp.imageUrl}
                      alt={gp?.name}
                      className="w-14 h-14 object-contain rounded border bg-white flex-shrink-0"
                    />
                  )}
                  <div>
                    <p className="font-bold text-gray-800">{gp?.name || "Produit inconnu"}</p>
                    {gp?.barcode && (
                      <p className="text-xs text-gray-400">🔢 {gp.barcode}</p>
                    )}
                    {gp?.category && (
                      <p className="text-xs text-gray-500">🏷️ {gp.category}</p>
                    )}
                  </div>
                </div>

                {/* ── Mode lecture ── */}
                {!isEditing && (
                  <div className="text-sm space-y-1 mb-3">
                    <p className="text-gray-700">
                      💶 Prix :{" "}
                      <strong>{link.price != null ? `${link.price} €` : "Non renseigné"}</strong>
                    </p>
                    <p className="text-gray-700">
                      📦 Stock :{" "}
                      <span className={`font-semibold ${
                        link.stock === "promotion" ? "text-green-600"
                        : link.stock === "indisponible" ? "text-red-600"
                        : "text-gray-800"
                      }`}>
                        {STOCK_LABELS[link.stock] || "Disponible"}
                      </span>
                    </p>
                    {link.customNote && (
                      <p className="text-gray-500 italic text-xs">"{link.customNote}"</p>
                    )}
                  </div>
                )}

                {/* ── Mode édition ── */}
                {isEditing && (
                  <div className="space-y-2 mb-3">
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={editForm.price}
                        onChange={(e) => setEditForm(prev => ({ ...prev, price: e.target.value }))}
                        placeholder="Prix (€)"
                        className="input-field flex-1"
                      />
                      <select
                        value={editForm.stock}
                        onChange={(e) => setEditForm(prev => ({ ...prev, stock: e.target.value }))}
                        className="input-field"
                      >
                        <option value="en stock">Disponible</option>
                        <option value="promotion">Promotion</option>
                        <option value="indisponible">Indisponible</option>
                      </select>
                    </div>
                    <input
                      type="text"
                      value={editForm.customNote}
                      onChange={(e) => setEditForm(prev => ({ ...prev, customNote: e.target.value }))}
                      placeholder="Note personnalisée"
                      className="input-field w-full"
                    />
                  </div>
                )}

                {/* ── Actions ── */}
                <div className="flex gap-2 flex-wrap">
                  {!isEditing && (
                    <button
                      onClick={() => startEdit(link)}
                      className="px-3 py-1 bg-indigo-500 text-white rounded text-sm hover:bg-indigo-600"
                    >
                      ✏️ Modifier
                    </button>
                  )}

                  {isEditing && (
                    <>
                      <button
                        onClick={() => saveEdit(link._id)}
                        disabled={saving}
                        className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50"
                      >
                        {saving ? "⏳ Enregistrement..." : "💾 Enregistrer"}
                      </button>
                      <button
                        onClick={cancelEdit}
                        disabled={saving}
                        className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400"
                      >
                        Annuler
                      </button>
                    </>
                  )}

                  {!isEditing && (
                    <button
                      onClick={() => deleteLink(link._id, gp?.name)}
                      disabled={isDeleting}
                      className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600 disabled:opacity-50"
                    >
                      {isDeleting ? "⏳..." : "🗑️ Supprimer"}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default VendorLinkedProducts;
